import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { apiGetBranchContractSummary } from "@api/api_branch_contract";
import { buildDevSummary, getDevScenarioKey } from "./branchContractDevScenarios";

/**
 * 右上角提醒與分店卡片共用同一份摘要；換頁不重打 API，5 分鐘後或加值後才重抓。
 * 依 token 區分快取，換帳號登入不會看到上一個帳號的分店。
 */
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = { token: null, data: null, fetchedAt: 0, promise: null };
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener());
}

function fetchSummary(token, { force = false } = {}) {
  const fresh = cache.token === token && cache.data && Date.now() - cache.fetchedAt < CACHE_TTL_MS;
  if (!force && fresh) return Promise.resolve(cache.data);
  if (!force && cache.token === token && cache.promise) return cache.promise;

  cache.token = token;
  const promise = apiGetBranchContractSummary()
    .then((res) => {
      if (cache.promise !== promise) return cache.data;
      cache.data = res?.data?.data || null;
      cache.fetchedAt = Date.now();
      return cache.data;
    })
    .finally(() => {
      if (cache.promise === promise) cache.promise = null;
      notify();
    });
  cache.promise = promise;
  return promise;
}

/** 加值訂單建立或其他會改變點數的動作之後呼叫 */
export function invalidateBranchContractSummary() {
  cache.fetchedAt = 0;
  if (cache.token) fetchSummary(cache.token, { force: true }).catch(() => {});
}

export function resetBranchContractSummaryCache() {
  cache.token = null;
  cache.data = null;
  cache.fetchedAt = 0;
  cache.promise = null;
}

export function useBranchContractSummary() {
  const token = useSelector((state) => state.user?.user?.token);
  const branches = useSelector((state) => state.user?.user?.brandInfo?.branches);
  const devScenario = getDevScenarioKey();
  const [, setVersion] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  useEffect(() => {
    if (!token || devScenario) return;
    fetchSummary(token).then(() => setError(null), (err) => setError(err));
  }, [token, devScenario]);

  const refresh = useCallback(() => {
    if (!token || devScenario) return Promise.resolve();
    return fetchSummary(token, { force: true }).then(() => setError(null), (err) => setError(err));
  }, [token, devScenario]);

  const devData = useMemo(
    () => (devScenario ? buildDevSummary(devScenario, branches || []) : null),
    [devScenario, branches]
  );

  const data = devData || (cache.token === token ? cache.data : null);
  const byBranchId = useMemo(() => {
    const map = new Map();
    (data?.branches || []).forEach((branch) => map.set(Number(branch.branchId), branch));
    return map;
  }, [data]);

  return {
    data,
    byBranchId,
    loading: !data && !error && Boolean(token),
    error,
    refresh,
  };
}
